// @section 1 Campaigns / GA4 — visitor traffic across every owned web property,
// classified bot vs human. Live read from /api/hq/campaigns/ga4 (backed by
// internal_warehouse.raw.ga4_bq_events with the canonical datacenter-city +
// null-geo + bot-source classifier). Previously fixture-backed; the fixture
// path retired 2026-06-04.
//
// Bot heuristic (in the backend SQL — block 1.17 of HqCampaignsEndpoints.cs):
//   * geo_city in the datacenter-city blocklist
//   * null geo_city + no engagement_time
//   * session_source matches bot/crawler/spider

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  getCampaignGa4Live,
  CampaignsUnconfigured,
  type Ga4LiveSnapshot,
  type Ga4LiveSite,
  type Ga4LiveDailySeries,
} from '../../api';
import { useIsMobile } from '../../lib/useIsMobile';

const KNOWN_SITES: { host: string; label: string; brand: string }[] = [
  { host: 'atlas.optiq-os.com',         label: 'Atlas',                   brand: 'NaviPoint' },
  { host: 'app.optiq-os.com',           label: 'OptiqOS App',             brand: 'NaviPoint' },
  { host: 'demo.optiq-os.com',          label: 'Demo Library',            brand: 'NaviPoint' },
  { host: 'demo4.optiq-os.com',         label: 'Demo v4 (cold-email)',    brand: 'NaviPoint' },
  { host: 'demo5.optiq-os.com',         label: 'Demo v5',                 brand: 'NaviPoint' },
  { host: 'audit.optiq-os.com',         label: 'Audit / Teardowns',      brand: 'NaviPoint' },
  { host: 'intel.optiq-os.com',         label: 'Building Intel',          brand: 'NaviPoint' },
  { host: 'go.optiq-os.com',            label: 'Go (magic-link)',         brand: 'NaviPoint' },
  { host: 'signal-rev.com',             label: 'Signal Rev',              brand: 'SignalLab' },
  { host: 'navipoint-technologies.com', label: 'NaviPoint Technologies', brand: 'NaviPoint' },
  { host: 'vertis-group.com',           label: 'Vertis Group',            brand: 'Vertis Group' },
  { host: 'vertisholdings.com',         label: 'Vertis Holdings',         brand: 'Vertis Holdings' },
  { host: 'conversiv.com',              label: 'Conversiv',               brand: 'Conversiv' },
  { host: 'domusosapp.com',             label: 'DomusOS',                 brand: 'NaviPoint' },
];

function brandFor(host: string): string {
  return KNOWN_SITES.find((s) => s.host === host || `www.${s.host}` === host)?.brand ?? '—';
}
function labelFor(host: string): string {
  return KNOWN_SITES.find((s) => s.host === host || `www.${s.host}` === host)?.label ?? host;
}
function fmtN(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function Ga4() {
  const [ga4, setGa4] = useState<Ga4LiveSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCampaignGa4Live()
      .then((d) => { if (!cancelled) setGa4(d); })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof CampaignsUnconfigured) setUnconfigured(true);
        else setErr(String(e));
      });
    return () => { cancelled = true; };
  }, []);

  if (unconfigured || err || !ga4) {
    return (
      <div className="v5-page">
        <div className="page-head">
          <div className="crumb"><b>Atlas</b><span className="sep">›</span><span>Campaigns</span><span className="sep">›</span><b>GA4</b></div>
          <div className="page-title">
            <h1>GA4 — Owned Properties</h1>
            <span className="sub">
              {unconfigured ? 'Pipeline not configured' : err ? 'Load failed' : 'Loading live snapshot…'}
            </span>
          </div>
        </div>
        {err && (
          <div className="proto-detail-body">
            <div style={{ padding: 12, border: '1px solid var(--neg)', borderRadius: 8, color: 'var(--neg)' }}>{err}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="v5-page">
      <div className="page-head">
        <div className="crumb"><b>Atlas</b><span className="sep">›</span><span>Campaigns</span><span className="sep">›</span><b>GA4</b></div>
        <div className="page-title">
          <h1>GA4 — Owned Properties</h1>
          <span className="sub">
            {ga4.window_days}-day window · as of {new Date(ga4.as_of).toLocaleString()} · {ga4.sites.length} hosts seen
          </span>
        </div>
      </div>

      <div className="proto-detail-body">
        <ToplineStrip ga4={ga4} />

        <div className="proto-detail-grid" style={{ marginTop: 14 }}>
          <Module slug="ga4-bot-human-split" title="Visitors · bot vs human" span={2}>
            <SiteTable sites={ga4.sites} />
          </Module>

          <Module slug="ga4-traffic-trend" title="Daily trend · stacked bot/human (14d)" span={2}>
            <TrendCharts daily={ga4.daily} />
          </Module>

          <Module slug="ga4-bq-freshness" title="BigQuery export freshness">
            <table className="proto-table">
              <thead><tr><th>Property</th><th>Stream</th><th>Last event</th><th className="num">Days behind</th></tr></thead>
              <tbody>
                {ga4.freshness.map((f) => (
                  <tr key={`${f.property_id}-${f.stream_id}`}>
                    <td className="mono">{f.property_id}</td>
                    <td className="mono">{f.stream_id}</td>
                    <td className="mono">{f.last_event}</td>
                    <td className="num" style={{ color: f.days_behind > 2 ? 'var(--neg)' : 'var(--tx-1)' }}>{f.days_behind}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 10, color: 'var(--tx-4)', marginTop: 8 }}>
              Source: <code>internal_warehouse.raw.ga4_bq_events</code>. Properties not in this list
              don't have BigQuery export enabled (separate backend task).
            </div>
          </Module>

          <Module slug="ga4-conversion" title="Conversion events">
            <Sketch>
              <code>page_view</code> · <code>scroll</code> · <code>generate_lead</code>
              · custom: <code>book_meeting_click</code>, <code>cta_hit</code>,
              <code>human_confirmed</code>, <code>magic_link_click</code>.
              Group by host. (Will wire after walk-through.)
            </Sketch>
          </Module>

          <Module slug="ga4-acquisition" title="Acquisition: source / medium / campaign">
            <Sketch>
              Top traffic sources per host. UTM-based join back to{' '}
              <code>master_label</code> for cold-email cohorts.
            </Sketch>
          </Module>

          <Module slug="ga4-top-pages" title="Top pages per site">
            <Sketch>
              Top 10 paths by visitors + engagement time, per host.
              Dead-page detector (high impressions, low engagement).
            </Sketch>
          </Module>

          <Module slug="ga4-cohorts" title="Cold-email cohorts (PID join)">
            <Sketch>
              demo4 only — join <code>page_location ?pid=lead_id</code> →{' '}
              <code>outreach_engine.serving.leads</code>. Pattern in{' '}
              <code>reference_ga4_pid_extraction.md</code>.
            </Sketch>
          </Module>

          <Module slug="ga4-property-roster" title="Property roster">
            <table className="proto-table">
              <thead><tr><th>Host</th><th>Label</th><th>Brand</th><th>Live in BQ?</th></tr></thead>
              <tbody>
                {KNOWN_SITES.map((s) => {
                  const live = ga4.sites.find((g) => g.host === s.host || g.host === `www.${s.host}`);
                  return (
                    <tr key={s.host}>
                      <td className="mono">{s.host}</td>
                      <td>{s.label}</td>
                      <td className="muted">{s.brand}</td>
                      <td>{live
                        ? <span className="conf conf-high">live</span>
                        : <span className="conf conf-failed">not flowing</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Module>
        </div>
      </div>
    </div>
  );
}

// @section 2 Top-line strip — 4 stats (2x2 on phones so the 28px numbers fit)
function ToplineStrip({ ga4 }: { ga4: Ga4LiveSnapshot }) {
  const t = ga4.totals;
  const isMobile = useIsMobile();
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: 1, background: 'var(--t2-border)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      <Stat label="Visitors" big={fmtN(t.visitors_total)} sub={`${ga4.window_days}d window`} />
      <Stat label="Human" big={fmtN(t.visitors_human)} sub={`${t.human_pct}%`} color="var(--pos)" />
      <Stat label="Bot" big={fmtN(t.visitors_bot)} sub={`${(100 - t.human_pct).toFixed(1)}%`} color="var(--neg)" />
      <Stat label="Events" big={fmtN(t.events_total)} sub={`${ga4.sites.length} hosts`} />
    </div>
  );
}

function Stat({ label, big, sub, color }: { label: string; big: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: 'rgba(10,19,34,0.5)', padding: '14px 16px' }}>
      <div style={{ fontSize: 10, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: 'var(--t-display)', fontSize: 28, fontWeight: 700, color: color ?? 'var(--tx-0)', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{big}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// @section 3 Per-site table — sortable on visitors / human%
function SiteTable({ sites }: { sites: Ga4LiveSite[] }) {
  type SK = 'host' | 'visitors_total' | 'visitors_human' | 'visitors_bot' | 'human_pct' | 'events_total' | 'last_event';
  const [sort, setSort] = useState<{ key: SK; dir: 'asc' | 'desc' }>({ key: 'visitors_total', dir: 'desc' });
  const rows = useMemo(() => {
    const arr = [...sites];
    arr.sort((a, b) => {
      const sign = sort.dir === 'asc' ? 1 : -1;
      const va = a[sort.key], vb = b[sort.key];
      if (typeof va === 'number' && typeof vb === 'number') return sign * (va - vb);
      return sign * String(va).localeCompare(String(vb));
    });
    return arr;
  }, [sites, sort]);

  const Th = ({ k, right, children }: { k: SK; right?: boolean; children: ReactNode }) => {
    const active = sort.key === k;
    const arrow = active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
    return (
      <th
        onClick={() => setSort({ key: k, dir: active && sort.dir === 'asc' ? 'desc' : 'asc' })}
        style={{ cursor: 'pointer', textAlign: right ? 'right' : 'left' }}
      >
        {children}{arrow}
      </th>
    );
  };

  return (
    <table className="cim-table">
      <thead>
        <tr>
          <Th k="host">Host</Th>
          <th>Label</th>
          <th>Brand</th>
          <Th k="visitors_total" right>Visitors</Th>
          <Th k="visitors_human" right>Human</Th>
          <Th k="visitors_bot"   right>Bot</Th>
          <Th k="human_pct"      right>Human %</Th>
          <Th k="events_total"   right>Events</Th>
          <Th k="last_event">Last</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.host} className="cim-row">
            <td className="mono">{s.host}</td>
            <td>{labelFor(s.host)}</td>
            <td className="muted">{brandFor(s.host)}</td>
            <td className="num">{s.visitors_total.toLocaleString()}</td>
            <td className="num" style={{ color: 'var(--pos)' }}>{s.visitors_human.toLocaleString()}</td>
            <td className="num" style={{ color: 'var(--neg)' }}>{s.visitors_bot.toLocaleString()}</td>
            <td className="num">
              <HumanPctBar pct={s.human_pct} />
            </td>
            <td className="num">{s.events_total.toLocaleString()}</td>
            <td className="mono">{s.last_event}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HumanPctBar({ pct }: { pct: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
      <div style={{ width: 60, height: 5, borderRadius: 3, background: 'var(--neg)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--pos)' }} />
      </div>
      <span style={{ minWidth: 38, textAlign: 'right' }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

// @section 4 Trend charts — one stacked bar chart per host (top N)
function TrendCharts({ daily }: { daily: Ga4LiveDailySeries[] }) {
  const TOP = 4;
  const top = daily.slice(0, TOP);
  if (top.length === 0) return <div className="proto-empty" style={{ fontStyle: 'italic', color: 'var(--tx-4)' }}>No daily data.</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
      {top.map((d) => (
        <TrendChart key={d.host} series={d} />
      ))}
    </div>
  );
}

function TrendChart({ series }: { series: Ga4LiveDailySeries }) {
  const W = 360, H = 130, padL = 28, padR = 8, padT = 16, padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const pts = series.series;
  if (pts.length === 0) return <div style={{ fontSize: 11, color: 'var(--tx-4)' }}>no data</div>;
  const max = Math.max(1, ...pts.map((p) => p.total));
  const bw = Math.max(2, (innerW / pts.length) - 2);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-1)' }}>{labelFor(series.host)}</span>
        <span style={{ fontSize: 10, color: 'var(--tx-4)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{series.host}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
        <line x1={padL} y1={padT + innerH} x2={W - padR} y2={padT + innerH} stroke="var(--t2-border)" />
        <text x={4} y={padT + 6} fontSize="9" fill="var(--tx-4)">{max}</text>
        {pts.map((p, i) => {
          const x = padL + i * (innerW / pts.length);
          const hH = (innerH * p.human) / max;
          const bH = (innerH * p.bot) / max;
          const yHumanTop = padT + innerH - hH;
          const yBotTop = yHumanTop - bH;
          return (
            <g key={p.date}>
              {p.human > 0 && <rect x={x} y={yHumanTop} width={bw} height={hH} fill="var(--pos)" opacity="0.85" />}
              {p.bot   > 0 && <rect x={x} y={yBotTop}   width={bw} height={bH} fill="var(--neg)" opacity="0.85" />}
              {i % 2 === 0 && (
                <text x={x + bw / 2} y={H - 6} fontSize="9" fill="var(--tx-4)" textAnchor="middle">
                  {p.date.slice(5)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// @section 5 Primitives
function Module({ slug, title, span, children }: { slug: string; title: string; span?: 1 | 2; children: ReactNode }) {
  return (
    <section className={`proto-module${span === 2 ? ' span-2' : ''}`}>
      <header>
        <h3>{title}</h3>
        <span className="mod-tag">module: <code>{slug}</code></span>
      </header>
      <div className="proto-module-body">{children}</div>
    </section>
  );
}

function Sketch({ children }: { children: ReactNode }) {
  return (
    <div style={{
      border: '1px dashed rgba(255,255,255,0.10)',
      borderRadius: 8,
      padding: 14,
      fontSize: 12,
      color: 'var(--tx-3)',
      lineHeight: 1.55,
    }}>
      {children}
    </div>
  );
}
